import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Alert,
  DialogFooter,
  Button,
  AlertDescription,
  AlertTitle,
  Input,
  FormControl,
  FormItem,
  FormLabel,
  Form,
} from '@predoc/cow';
import { CircleX, Info } from 'lucide-react';
import { User } from '../types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { toast } from 'sonner';
import { useDeactivateUser } from '../hooks/usersApi';
import { queryClient, trpc } from '@/utils/trpc';

const deactivateUserSchema = (userEmailAddress: string) =>
  z.object({
    emailAddress: z
      .email('Please enter a valid email address')
      .refine(
        (emailAddress) => emailAddress === userEmailAddress,
        'Email does not match the user targeted for deactivation.',
      ),
  });

type DeactivateUserFormValues = z.infer<
  ReturnType<typeof deactivateUserSchema>
>;

export const DeactivateUserModal = ({
  open,
  handleClose,
  user,
}: {
  open: boolean;
  handleClose: () => void;
  user: User;
}) => {
  const fullName = `${user.firstName} ${user.lastName}`;
  const deactivateUser = useDeactivateUser();
  const form = useForm<DeactivateUserFormValues>({
    resolver: zodResolver(deactivateUserSchema(user.emailAddress)),
    defaultValues: {
      emailAddress: '',
    },
  });

  const handleModalClose = () => {
    form.reset();
    handleClose();
  };

  const handleDeactivate = () => {
    deactivateUser.mutate(
      { userId: user.id },
      {
        onSuccess: () => {
          toast.success(`${fullName} was deactivated.`);
          queryClient.invalidateQueries({
            queryKey: trpc.usersRouter.getOrganizationUsers.queryKey(),
          });
          handleModalClose();
        },
        onError: (error) => {
          toast.error(`${fullName} was not deactivated.`);
          console.error(error);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleModalClose}>
      <DialogContent className="mx-auto !max-w-[946px]">
        {' '}
        {/** TODO: remove !max-w-[946px] when COW fixes this*/}
        <DialogHeader className="border-border -mx-2.5 border-b p-2.5">
          <DialogTitle>Deactivate User</DialogTitle>
        </DialogHeader>
        <Alert variant="destructive">
          <Info className="size-4" />
          <AlertTitle className="line-clamp-none">
            {' '}
            {/** TODO: remove line-clamp-none when COW fixes this*/}
            Confirm Deactivation of {fullName}.{' '}
            <span className="font-bold">This cannot be undone.</span>
          </AlertTitle>
          <AlertDescription>
            This user will lose access to all profile and patient data in this
            organization&apos;s account. To proceed, enter the user&apos;s email
            address and confirm deactivation.
          </AlertDescription>
        </Alert>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleDeactivate)}>
            <FormItem>
              <FormLabel
                className={
                  form.formState.errors.emailAddress ? 'text-destructive' : ''
                }
              >
                User&apos;s Email *
              </FormLabel>
              <FormControl>
                <div className="flex">
                  <Input
                    {...form.register('emailAddress')}
                    placeholder="user@email.com"
                    className={`mt-2 w-1/2 ${form.formState.errors.emailAddress ? 'border-destructive' : ''}`}
                  />
                </div>
              </FormControl>
              {form.formState.errors.emailAddress && (
                <p className="text-destructive mt-1 text-sm">
                  {form.formState.errors.emailAddress.message}
                </p>
              )}
            </FormItem>
            <DialogFooter className="border-border -mx-2.5 mt-2.5 border-t px-2.5 pt-2">
              {Object.keys(form.formState.errors).length > 0 && (
                <div className="text-destructive flex items-center gap-2">
                  <CircleX className="size-4" />
                  Please fix validation errors.
                </div>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={handleModalClose}
              >
                Cancel
              </Button>
              <Button type="submit" variant="destructive">
                Deactivate
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
